import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { DienstnehmerService, Dienstnehmer } from '@app/services/dienstnehmer.service';
import { BeuserService, Beuser } from '@app/services/beuser.service';
import { DeviceService, Device } from '@app/services/device.service';
import { ROLES, ROLE_ADMIN, ROLE_ORGAN, roleBadgeClasses, roleBadgeLabel } from '@app/@shared/roles';

@Component({
  selector: 'app-dienstnehmer',
  templateUrl: './dienstnehmer.component.html',
  styleUrls: ['./dienstnehmer.component.scss'],
})
export class DienstnehmerComponent implements OnInit {
  list: Dienstnehmer[] = [];
  loading = false;
  saving = false;

  // form model (id 0 => new)
  edit: Dienstnehmer = this.emptyDienstnehmer();

  // role labels for the benutzergruppe select (aligned with Rollen & Rechte matrix)
  roleOptions = ROLES.map((r) => ({ value: r.id, label: `${r.id} - ${r.name}` }));
  // Web login accounts (t_beuser) — typically not Organ (field officers)
  webRoleOptions = ROLES.filter((r) => r.id !== ROLE_ORGAN).map((r) => ({
    value: r.id,
    label: `${r.id} - ${r.name}`,
  }));
  readonly roleOrgan = ROLE_ORGAN;

  // ---- Mitarbeiter tab (t_beuser) ----
  mitarbeiterList: Beuser[] = [];
  mitarbeiterLoading = false;
  mitarbeiterSaving = false;
  mitarbeiterEdit: Beuser = this.emptyMitarbeiter();

  groupLabel(value: number): string {
    return roleBadgeLabel(value);
  }

  groupBadgeClasses(value: number): string {
    return roleBadgeClasses(value);
  }

  // ---- Devices tab ----
  devices: Device[] = [];
  devicesLoading = false;
  deviceEdit: Device = this.emptyDevice();
  deviceSaving = false;

  get activeDienstnehmer(): Dienstnehmer[] {
    return this.list.filter((dn) => dn.active === 1);
  }

  get pendingDevices(): Device[] {
    return this.devices.filter((d) => d.active !== 1 && !d.last_seen);
  }

  get managedDevices(): Device[] {
    return this.devices.filter((d) => d.active === 1 || !!d.last_seen);
  }

  deviceCountFor(dienstnummer: number): number {
    if (!dienstnummer) {
      return 0;
    }
    return this.devices.filter((d) => d.dienstnummer === dienstnummer).length;
  }

  dienstnehmerLabelFor(dienstnummer: number | null | undefined): string {
    if (dienstnummer == null || dienstnummer === 0) {
      return '–';
    }
    const dn = this.list.find((row) => row.dienstnummer === dienstnummer);
    return dn ? `${dn.name} (${dienstnummer})` : String(dienstnummer);
  }

  deviceStatusLabel(d: Device): string {
    return d.active === 1 ? 'Aktiv' : 'Gesperrt';
  }

  deviceStatusClasses(d: Device): string {
    return d.active === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  }

  deviceToggleLabel(d: Device): string {
    return d.active === 1 ? 'Sperren' : 'Entsperren';
  }

  formatLastSeen(value?: string | null): string {
    if (!value) {
      return '–';
    }
    const normalized = value.replace(' ', 'T');
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  mitarbeiterRoleBadgeClasses(user: Beuser): string {
    return roleBadgeClasses(user.role_id);
  }

  dienstnehmerNameFor(fId: number | null | undefined): string {
    if (!fId) {
      return '–';
    }
    const dn = this.list.find((row) => row.id === fId);
    return dn ? dn.name : String(fId);
  }

  constructor(
    private dienstnehmerService: DienstnehmerService,
    private beuserService: BeuserService,
    private deviceService: DeviceService,
    private _snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadList();
    this.loadMitarbeiter();
    this.loadDevices();
  }

  loadList(): void {
    this.loading = true;
    this.dienstnehmerService.getAll().subscribe(
      (rows) => {
        this.list = rows;
        this.loading = false;
      },
      () => {
        this.loading = false;
        this.notify('Dienstnehmer konnten nicht geladen werden.');
      }
    );
  }

  newDienstnehmer(): void {
    this.edit = this.emptyDienstnehmer();
  }

  selectForEdit(dn: Dienstnehmer): void {
    // copy so the table row is not mutated until save
    this.edit = { ...dn, kennwort: '' };
  }

  save(): void {
    if (!this.edit.name || ('' + this.edit.name).trim() === '') {
      this.notify('Bitte einen Namen angeben.');
      return;
    }
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.dienstnehmerService.save(this.edit).subscribe(
      (res) => {
        this.saving = false;
        if (res?.result === 1) {
          this.notify('Dienstnehmer gespeichert.');
          this.newDienstnehmer();
          this.loadList();
        } else {
          this.notify('Fehler: ' + (res?.error || 'unbekannt'));
        }
      },
      () => {
        this.saving = false;
        this.notify('Dienstnehmer konnte nicht gespeichert werden.');
      }
    );
  }

  remove(dn: Dienstnehmer): void {
    if (!dn.id) {
      return;
    }
    if (!confirm(`Dienstnehmer "${dn.name}" wirklich löschen?`)) {
      return;
    }
    this.dienstnehmerService.delete(dn.id).subscribe(
      (res) => {
        if (res?.result === 1) {
          this.notify('Dienstnehmer gelöscht.');
          if (this.edit.id === dn.id) {
            this.newDienstnehmer();
          }
          this.loadList();
        } else {
          this.notify('Fehler: ' + (res?.error || 'unbekannt'));
        }
      },
      () => this.notify('Dienstnehmer konnte nicht gelöscht werden.')
    );
  }

  // ----------------------------------------------------------- Mitarbeiter tab

  loadMitarbeiter(): void {
    this.mitarbeiterLoading = true;
    this.beuserService.getAll().subscribe(
      (rows) => {
        this.mitarbeiterList = rows;
        this.mitarbeiterLoading = false;
      },
      () => {
        this.mitarbeiterLoading = false;
        this.notify('Office-Benutzer konnten nicht geladen werden.');
      }
    );
  }

  newMitarbeiter(): void {
    this.mitarbeiterEdit = this.emptyMitarbeiter();
  }

  selectMitarbeiterForEdit(user: Beuser): void {
    this.mitarbeiterEdit = { ...user, pwd: '' };
  }

  saveMitarbeiter(): void {
    if (!this.mitarbeiterEdit.name || ('' + this.mitarbeiterEdit.name).trim() === '') {
      this.notify('Bitte einen Namen angeben.');
      return;
    }
    if (!this.mitarbeiterEdit.login || ('' + this.mitarbeiterEdit.login).trim() === '') {
      this.notify('Bitte einen Login angeben.');
      return;
    }
    if (this.mitarbeiterSaving) {
      return;
    }
    this.mitarbeiterSaving = true;
    this.beuserService.save(this.mitarbeiterEdit).subscribe(
      (res) => {
        this.mitarbeiterSaving = false;
        if (res?.result === 1) {
          this.notify('Office-Benutzer gespeichert.');
          this.newMitarbeiter();
          this.loadMitarbeiter();
        } else {
          this.notify('Fehler: ' + (res?.error || 'unbekannt'));
        }
      },
      () => {
        this.mitarbeiterSaving = false;
        this.notify('Office-Benutzer konnte nicht gespeichert werden.');
      }
    );
  }

  deactivateMitarbeiter(user: Beuser): void {
    if (!user.id) {
      return;
    }
    if (!confirm(`Office-Benutzer "${user.name}" wirklich deaktivieren?`)) {
      return;
    }
    this.beuserService.deactivate(user.id).subscribe(
      (res) => {
        if (res?.result === 1) {
          this.notify('Office-Benutzer deaktiviert.');
          if (this.mitarbeiterEdit.id === user.id) {
            this.newMitarbeiter();
          }
          this.loadMitarbeiter();
        } else {
          this.notify('Fehler: ' + (res?.error || 'unbekannt'));
        }
      },
      () => this.notify('Office-Benutzer konnte nicht deaktiviert werden.')
    );
  }

  // ----------------------------------------------------------- Devices tab

  loadDevices(): void {
    this.devicesLoading = true;
    this.deviceService.getAll().subscribe(
      (rows) => {
        this.devices = rows;
        this.devicesLoading = false;
      },
      () => {
        this.devicesLoading = false;
        this.notify('Geräte konnten nicht geladen werden.');
      }
    );
  }

  newDevice(): void {
    this.deviceEdit = this.emptyDevice();
  }

  selectDeviceForEdit(d: Device): void {
    this.deviceEdit = { ...d, token: '' };
  }

  saveDevice(): void {
    if (!this.deviceEdit.phone_id || ('' + this.deviceEdit.phone_id).trim() === '') {
      this.notify('Bitte eine phone_id angeben.');
      return;
    }
    if (this.deviceSaving) {
      return;
    }
    this.deviceSaving = true;
    this.deviceService.save(this.deviceEdit).subscribe(
      (res) => {
        this.deviceSaving = false;
        if (res?.result === 1) {
          this.notify('Gerät gespeichert.');
          this.newDevice();
          this.loadDevices();
        } else {
          this.notify('Fehler: ' + (res?.error || 'unbekannt'));
        }
      },
      () => {
        this.deviceSaving = false;
        this.notify('Gerät konnte nicht gespeichert werden.');
      }
    );
  }

  toggleDeviceActive(d: Device): void {
    if (!d.id) {
      return;
    }
    const newActive = d.active === 1 ? 0 : 1;
    this.deviceService.setActive(d.id, newActive).subscribe(
      (res) => {
        if (res?.result === 1) {
          d.active = newActive;
          this.loadDevices();
          this.notify(newActive === 1 ? 'Gerät aktiviert.' : 'Gerät gesperrt.');
        } else {
          this.notify('Fehler: ' + (res?.error || 'unbekannt'));
        }
      },
      () => this.notify('Aktivierung fehlgeschlagen.')
    );
  }

  removeDevice(d: Device): void {
    if (!d.id) {
      return;
    }
    if (!confirm(`Gerät "${d.phone_id}" wirklich löschen?`)) {
      return;
    }
    this.deviceService.delete(d.id).subscribe(
      (res) => {
        if (res?.result === 1) {
          this.notify('Gerät gelöscht.');
          if (this.deviceEdit.id === d.id) {
            this.newDevice();
          }
          this.loadDevices();
        } else {
          this.notify('Fehler: ' + (res?.error || 'unbekannt'));
        }
      },
      () => this.notify('Gerät konnte nicht gelöscht werden.')
    );
  }

  private emptyDevice(): Device {
    return {
      id: 0,
      phone_id: '',
      dienstnummer: null,
      label: '',
      active: 0,
      token: '',
    };
  }

  private emptyMitarbeiter(): Beuser {
    return {
      id: 0,
      name: '',
      login: '',
      email: '',
      beusergroup_id: ROLE_ADMIN,
      role_id: ROLE_ADMIN,
      role_name: 'Admin',
      admin: 1,
      su: 0,
      f_id: 0,
      active: 1,
      isapproved: 1,
      isactivated: 1,
      pwd: '',
    };
  }

  private emptyDienstnehmer(): Dienstnehmer {
    return {
      id: 0,
      name: '',
      dienstnummer: 0,
      dienstnummer_stvo: 0,
      active: 1,
      benutzergruppe: 4,
      sachbereich: '',
      kennwort: '',
    };
  }

  private notify(msg: string): void {
    this._snackBar.open(msg, '', { duration: 3000, horizontalPosition: 'center', verticalPosition: 'top' });
  }
}
